<script setup lang="ts">
import print from '@/foundations/helpers/print';
import axios from 'axios';
import { stopAllSounds } from '@ilteducation/audio-resources';
import config from '@/config/ConfigProvider';
import { capitalize, computed, ref, watch } from 'vue';
import { useElementSize } from '@vueuse/core';
import RegularView from '@/components/videos/keywordViews/RegularView.vue';
import useKeywordsStore, { KeywordWithTranslations } from '@/store/keywords';
import useLanguagesStore from '@/store/languages';
import { useLocale } from '@/plugins/translation';
import type { Video } from '@ilteducation/videos';
import uniq from 'lodash/uniq';
import useAudioStore from '@/store/audio';
import FlashCardView from './keywordViews/FlashCardView.vue';
import PrintKeyWords from './PrintKeyWords.vue';
import { useImages } from '@ilteducation/images';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { trackTabAction } from '@/composables/use-video-tab-analytics';

const props = defineProps<{
  video: Video;
}>();

const isExplanationVisible = ref(true);
const keywordsViewOptions = ref({
  regular: 'regular',
  flashCards: 'flashCards',
});
const keywordsView = ref('regular');

const audioStore = useAudioStore();
const { t, l } = useLocale();

const filmKeywords = ref<KeywordWithTranslations[]>([]);
const altTexts = ref<Record<string, string>>({});

watch(
  () => props.video,
  async () => {
    filmKeywords.value = await useKeywordsStore().getKeywordTranslations(props.video.keywords);

    const images = filmKeywords.value.map((k) => k.imageId).filter((id): id is string => !!id);
    altTexts.value = await useImages().getAltTextsTranslations(images, getMarketLanguage());
  },
  { immediate: true },
);

const { getLanguageByShortcode, getMarketLanguage } = useLanguagesStore();
const keywordLanguages = computed(() =>
  uniq(filmKeywords.value.flatMap((kw) => kw.supportedLanguages))
    .toSorted((a, b) => translateLanguageName(a).localeCompare(translateLanguageName(b)))
    .map((k) => ({ value: k })),
);
const selectedTranslation = ref();
const translateLanguageName = (languageCode: string) =>
  languageCode in l.languages ? t(l.languages[languageCode as keyof typeof l.languages] as string) : languageCode; // get language name in current UI language

const updateAudioResources = (items: { id: string; key: string; language: string }[]) => {
  items.forEach((el) => {
    if (el.id) {
      audioStore.addAudioResources({
        url: '',
        key: el.key,
        id: el.id,
        lang: el.language === 'swe' ? 'sv' : el.language,
      });
    }
  });
};

const getKeywordsAudio = computed(() => () => {
  const keywordsNames: string[] = [];
  const keywordsDescriptions: string[] = [];
  filmKeywords.value.forEach((el) => {
    if (
      !audioStore.audioResources.find(
        (audio: { key: string }) => audio.key === el.title || audio.key === el.description,
      )
    ) {
      keywordsNames.push(`ref=${encodeURIComponent(el.title)}`);
      if (el.description) {
        keywordsDescriptions.push(`ref=${encodeURIComponent(el.description)}`);
      }
    }
  });

  if (keywordsNames.length) {
    axios
      .get(`${config.value('audioCentralUrl')}?${keywordsNames.join('&')}&language=swe&recType=voice`)
      .then((namesResp) => updateAudioResources(namesResp.data));
  }

  if (keywordsDescriptions.length) {
    axios(`${config.value('audioCentralUrl')}?${keywordsDescriptions.join('&')}&language=swe&recType=voice`).then(
      (response) => updateAudioResources(response.data),
    );
  }
});

const selectedNative = ref(getMarketLanguage() === 'nb' ? 'no-nb' : (getMarketLanguage() as keyof typeof l.languages));

const filteredAndSortedKeywords = computed(() => {
  const res = filmKeywords.value.filter((kw) => kw.supportedLanguages.includes(selectedNative.value));
  res.sort((a, b) =>
    a.translations.title[selectedNative.value].translation.localeCompare(
      b.translations.title[selectedNative.value].translation,
    ),
  );
  return res;
});

watch(
  filmKeywords,
  () => {
    getKeywordsAudio.value();
  },
  { immediate: true },
);
const printAction = () => {
  trackTabAction('keywords', 'print');
  print('js_print-keywords');
};
const switchView = () => {
  stopAllSounds();
  if (keywordsView.value === keywordsViewOptions.value.regular) {
    keywordsView.value = keywordsViewOptions.value.flashCards;
    trackTabAction('keywords', 'open_flashcards');
  } else {
    keywordsView.value = keywordsViewOptions.value.regular;
  }
};

const onTranslationLanguageChange = (languageCode: string | null) => {
  if (!languageCode) {
    trackTabAction('keywords', 'clear_translation_language');
    return;
  }
  trackTabAction('keywords', 'select_translation_language', languageCode);
};

// The keyword columns below reserve the same right-hand slot width as this action-button
// group, so their language columns line up exactly with the language dropdowns above.
const buttonGroupRef = ref<HTMLElement | null>(null);
const { width: asideWidth } = useElementSize(buttonGroupRef);

// "Native language" is in this context actually the selected language in the left dropdown
const translatedNativeLanguageName = computed(() =>
  t(l.languages?.[selectedNative.value as keyof typeof l.languages] as string),
);

// Similarly, "Translation language" is the selected language in the right dropdown
const translatedTranslationLanguageName = computed(() =>
  t(l.languages?.[selectedTranslation.value as keyof typeof l.languages] as string),
);
</script>
<template>
  <v-container>
    <v-row v-if="!keywordLanguages.length || !video" style="min-height: 300px">
      <v-col cols="12" sm="6" class="pr-sm-3 mb-2 mb-md-0">
        <v-skeleton-loader type="heading" width="50%"></v-skeleton-loader>
        <v-skeleton-loader type="sentences" width="100%"></v-skeleton-loader>
        <v-skeleton-loader type="heading" width="50%"></v-skeleton-loader>
        <v-skeleton-loader type="sentences" width="100%"></v-skeleton-loader>
      </v-col>
    </v-row>
    <v-row v-else style="min-height: 300px" class="mx-0 align-center">
      <v-col cols="12" class="py-0">
        <div class="d-flex flex-column flex-md-row ga-3 align-md-center">
          <v-select
            v-model="selectedNative"
            :items="keywordLanguages"
            :item-title="({ value }) => capitalize(translateLanguageName(value))"
            density="comfortable"
            color="secondary"
            rounded="false"
            :list-props="{ bgColor: 'rgb(var(--v-theme-surface-container-lowest))' }"
            :label="t(l.choose_language)"
            hide-details
            single-line
            class="flex-md-grow-1 keyword-lang-select"
          >
          </v-select>
          <transition name="fade">
            <v-select
              v-model="selectedTranslation"
              :items="keywordLanguages"
              :item-title="({ value }) => capitalize(translateLanguageName(value))"
              color="secondary"
              density="comfortable"
              rounded="false"
              :list-props="{ bgColor: 'rgb(var(--v-theme-surface-container-lowest))' }"
              :placeholder="t(l.choose_language)"
              :label="t(l.choose_language)"
              hide-details
              single-line
              clearable
              class="flex-md-grow-1 keyword-lang-select"
              @update:model-value="onTranslationLanguageChange"
            >
            </v-select>
          </transition>
          <div ref="buttonGroupRef" class="d-flex ga-3 justify-md-end">
            <v-btn variant="outlined" class="text-none" color="secondary" @click="switchView()">
              {{ keywordsView === 'regular' ? t(l.film.keywordsSwitchView) : t(l.film.keywordsListView) }}
              <FontAwesomeIcon size="sm" class="ml-2" icon="fa-regular fa-right-left"></FontAwesomeIcon>
            </v-btn>
            <v-btn variant="outlined" class="text-none" color="secondary" @click="printAction()">
              {{ t(l.print) }}
              <FontAwesomeIcon size="sm" class="ml-2" icon="fa-regular fa-print"></FontAwesomeIcon>
            </v-btn>
          </div>
        </div>
      </v-col>
      <v-col v-if="keywordsView === keywordsViewOptions.regular" cols="12" class="py-0">
        <v-checkbox
          v-model="isExplanationVisible"
          class="show-explanations"
          color="secondary"
          hide-details
          :label="t(l.show_explanations)"
        >
        </v-checkbox>
      </v-col>
      <!--keywords view-->
      <v-col cols="12" class="pt-1">
        <RegularView
          v-if="keywordsView === keywordsViewOptions.regular"
          :selected-native="selectedNative"
          :selected-translation="selectedTranslation"
          :is-explanation-visible="isExplanationVisible"
          :keywords="filteredAndSortedKeywords"
          :alt-texts="altTexts"
          :aside-width="asideWidth"
        ></RegularView>
        <FlashCardView
          v-if="keywordsView === keywordsViewOptions.flashCards"
          :selected-native="selectedNative"
          :selected-translation="selectedTranslation"
          :is-explanation-visible="isExplanationVisible"
          :keywords="filteredAndSortedKeywords"
          :alt-texts="altTexts"
        ></FlashCardView>
      </v-col>
      <!--END keywords view-->
    </v-row>
    <div id="js_print-keywords" class="d-none" style="visibility: hidden">
      <PrintKeyWords v-if="filmKeywords" :video="video">
        <div>
          <RegularView
            v-if="keywordsView === keywordsViewOptions.regular"
            :selected-native="selectedNative"
            :selected-translation="selectedTranslation"
            :is-explanation-visible="isExplanationVisible"
            :keywords="filteredAndSortedKeywords"
            :alt-texts="altTexts"
            :print-view="true"
          ></RegularView>
          <FlashCardView
            v-else
            :selected-native="selectedNative"
            :selected-translation="selectedTranslation"
            :is-explanation-visible="isExplanationVisible"
            :keywords="filteredAndSortedKeywords"
            :alt-texts="altTexts"
            :print-view="true"
          ></FlashCardView>
        </div>
        <template #languages>
          <div class="print-keywords-breadcrumbs">
            <span>{{ translatedNativeLanguageName }}</span>
            <span v-if="getLanguageByShortcode(selectedTranslation)">
              {{ ` / ${translatedTranslationLanguageName}` }}
            </span>
          </div>
        </template>
      </PrintKeyWords>
    </div>
  </v-container>
</template>

<style lang="scss" scoped>
.show-explanations :deep(.v-selection-control) {
  min-height: auto;
}

@media (min-width: 960px) {
  .keyword-lang-select {
    flex-basis: 0 !important;
  }
}

:deep(.v-select .v-field) {
  background-color: rgb(var(--v-theme-surface-container-lowest));

  .v-field__overlay {
    opacity: 0;
  }
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter, .slide-fade-leave-to
  /* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateX(10px);
  opacity: 0;
}

.fade-enter-active {
  -webkit-transition: all 0.35s cubic-bezier(0.86, 0, 0.07, 1);
  -moz-transition: all 0.35s cubic-bezier(0.86, 0, 0.07, 1);
  -o-transition: all 0.35s cubic-bezier(0.86, 0, 0.07, 1);
  transition: all 0.35s cubic-bezier(0.86, 0, 0.07, 1);
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-leave,
.fade-leave-active {
  display: none;
}

.print-btn:hover {
  color: #265eb8;

  i {
    color: #265eb8;
  }
}

@page {
  margin-top: 146px; // align keyword rows across pages (first page-only header height + margin/padding)
}

@page :first {
  margin-top: 0; // align keyword rows across pages
}
</style>
